import { apiClient } from '../api/apiClient';
import type { TtsVoiceConfig } from './ttsService';

// Re-export TtsVoiceConfig for convenience
export type { TtsVoiceConfig };

/**
 * TTS Voice Info (from provider API)
 */
export interface TtsVoiceInfo {
  voiceId: string;
  displayName: string;
  gender: string;
  description: string;
}

/**
 * Bookseller TTS Configuration DTO
 */
export interface BooksellerTtsConfig {
  preferredTtsProvider: string;
  availableVoices: TtsVoiceConfig[];
}

/**
 * Update Bookseller TTS Configuration DTO
 */
export interface UpdateBooksellerTtsConfig {
  availableVoices: TtsVoiceConfig[];
}

/**
 * Bookseller Summary DTO
 */
export interface BooksellerSummary {
  id: string;
  name: string;
  displayName: string;
  isActive: boolean;
}

/**
 * Bookseller TTS Configuration Service
 * For admin to manage TTS voices available to students
 */
class BooksellerTtsService {
  /**
   * Get all active booksellers (for admin selection)
   */
  async getAllBooksellers(): Promise<BooksellerSummary[]> {
    const response = await apiClient.get<BooksellerSummary[]>(
      '/api/app/bookseller-tts-config/all-booksellers'
    );
    return response.data;
  }

  /**
   * Get current TTS configuration for the bookseller
   */
  async getConfiguration(booksellerId: string): Promise<BooksellerTtsConfig> {
    const response = await apiClient.get<BooksellerTtsConfig>(
      `/api/app/bookseller-tts-config?booksellerId=${booksellerId}`
    );
    return response.data;
  }

  /**
   * Update TTS configuration
   */
  async updateConfiguration(booksellerId: string, config: UpdateBooksellerTtsConfig): Promise<void> {
    await apiClient.put(`/api/app/bookseller-tts-config?booksellerId=${booksellerId}`, config);
  }

  /**
   * Get all voices from the TTS provider
   * Used for initial setup to show admin all available voices
   */
  async getProviderVoices(): Promise<TtsVoiceInfo[]> {
    const response = await apiClient.get<TtsVoiceInfo[]>(
      '/api/app/bookseller-tts-config/provider-voices'
    );
    return response.data;
  }
}

export const booksellerTtsService = new BooksellerTtsService();
